import { Brain, ChevronDown, ChevronUp, Loader2, Pencil, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { cn } from "@/shared/lib/cn";
import { resolveLegacyUploadUrl } from "@/shared/lib/legacyAssets";
import { InvoiceAiCorrectionDialog } from "./InvoiceAiCorrectionDialog";
import { useInvoiceAiAudit } from "../hooks/useInvoiceAiAudit";
import {
  downloadInvoiceRefractionXlsx,
  openInvoiceAiReport,
  openInvoiceRefractionSheet,
} from "../api/invoicePrintApi";
import type { InvoiceAiAnalysisRow } from "../api/auditorApi";
import {
  analysisRowAmounts,
  computeAiColumnTotals,
  computeAiMoneySummary,
  inferExchangeRateFromTable,
  isSynthesisRow,
  sortAnalysisTable,
} from "../lib/invoiceAiRowAmounts";

type InvoiceAiAuditPanelProps = {
  invoiceId: number;
  hospitalId?: number | null;
  isForfaitMensuel?: boolean;
  canDecide: boolean;
  onRetainedTotalChange?: (total: number | null) => void;
  onReviewsValidationChange?: (validation: { ok: boolean; error: string; pending: number } | null) => void;
};

function formatMoney(amount: number, currency: string): string {
  const decimals = currency === "CDF" ? 0 : 2;
  const suffix = currency === "CDF" ? " FC" : " USD";
  return `${amount.toLocaleString("fr-FR", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${suffix}`;
}

function obsLabel(type: string | undefined): string {
  switch (type) {
    case "surcharge":
      return "Surcharge";
    case "sous_facturation":
      return "Sous-fact.";
    case "non_tarifé":
      return "Non tarifé";
    case "sans_proforma":
      return "Sans proforma";
    case "synthese":
      return "Synthèse";
    default:
      return "OK";
  }
}

function obsClass(type: string | undefined): string {
  switch (type) {
    case "surcharge":
    case "sans_proforma":
      return "bg-red-50 text-red-700";
    case "sous_facturation":
      return "bg-amber-50 text-amber-800";
    case "non_tarifé":
      return "bg-card-muted text-muted-foreground";
    case "synthese":
      return "bg-slate-100 text-slate-700";
    default:
      return "bg-green-50 text-green-700";
  }
}

function legacyReportUrl(invoiceId: number, complete = false): string | null {
  const base = import.meta.env.VITE_LEGACY_ASSETS_URL?.replace(/\/$/, "");
  const path = `controllers/InvoiceReportController.php?id=${invoiceId}${complete ? "&mode=complete" : ""}`;
  if (base) return `${base}/${path}`;
  return resolveLegacyUploadUrl(path);
}

async function openAiReport(invoiceId: number, mode: "revised" | "complete"): Promise<void> {
  try {
    await openInvoiceAiReport(invoiceId, mode);
  } catch {
    const legacy = legacyReportUrl(invoiceId, mode === "complete");
    if (legacy) window.open(legacy, "_blank", "noopener,noreferrer");
  }
}

export function InvoiceAiAuditPanel({
  invoiceId,
  hospitalId,
  isForfaitMensuel,
  canDecide,
  onRetainedTotalChange,
  onReviewsValidationChange,
}: InvoiceAiAuditPanelProps) {
  const enabled = !isForfaitMensuel;
  const {
    context,
    loading,
    analyzing,
    saving,
    reanalyzing,
    error,
    reviews,
    corrections,
    manualRows,
    dirty,
    saveState,
    startAnalysis,
    saveAll,
    saveAndReanalyze,
    updateReview,
    updateCorrection,
    addManualRow,
    removeManualRow,
  } = useInvoiceAiAudit(invoiceId, enabled, { canReview: canDecide });

  const [expanded, setExpanded] = useState(true);
  const [showCorrections, setShowCorrections] = useState(false);
  const [retainedPreview, setRetainedPreview] = useState<number | null>(null);
  const [refractionError, setRefractionError] = useState<string | null>(null);
  const [obsFilter, setObsFilter] = useState<
    "all" | "anomalies" | "ok" | "sans_proforma" | "surcharge" | "sous_facturation" | "non_tarifé"
  >("all");

  const analysis = context?.analysis;
  const table = context?.analysis_table ?? [];
  const combinedTable = useMemo(
    () => sortAnalysisTable([...table, ...manualRows]),
    [manualRows, table],
  );
  const hasAnalysis = table.length > 0 || (analysis?.score ?? 0) > 0;
  const currency = context?.invoice_currency ?? "USD";
  const exchangeRateCdf = useMemo(() => {
    const fromApi = context?.exchange_rate_cdf ?? 0;
    if (fromApi > 0) return fromApi;
    return inferExchangeRateFromTable(context?.analysis_table ?? []);
  }, [context?.exchange_rate_cdf, context?.analysis_table]);
  const retained = retainedPreview ?? context?.retained_total ?? 0;
  const validation = context?.reviews_validation;
  const rateBlocked = context?.rate_blocks_ai?.blocked;

  const moneySummary = useMemo(
    () =>
      computeAiMoneySummary({
        rows: combinedTable,
        reviews,
        currency,
        exchangeRateCdf,
        billedTotal: context?.billed_total,
        retainedOverride: retained > 0 ? retained : null,
      }),
    [combinedTable, context?.billed_total, currency, exchangeRateCdf, retained, reviews],
  );

  const obsFilterCounts = useMemo(() => {
    const counts: Record<string, number> = {
      all: 0,
      anomalies: 0,
      ok: 0,
      sans_proforma: 0,
      surcharge: 0,
      sous_facturation: 0,
      "non_tarifé": 0,
    };
    for (const row of combinedTable) {
      if (isSynthesisRow(row)) continue;
      counts.all++;
      const obs = row.type_obs ?? "ok";
      if (obs !== "ok") counts.anomalies++;
      if (obs === "ok") counts.ok++;
      else if (obs in counts) counts[obs]++;
    }
    return counts;
  }, [combinedTable]);

  const filteredTable = useMemo(() => {
    return combinedTable.filter((row) => {
      if (isSynthesisRow(row)) return false;
      const obs = row.type_obs ?? "ok";
      switch (obsFilter) {
        case "all":
          return true;
        case "anomalies":
          return obs !== "ok";
        default:
          return obs === obsFilter;
      }
    });
  }, [combinedTable, obsFilter]);

  const columnTotals = useMemo(
    () =>
      computeAiColumnTotals({
        rows: filteredTable,
        reviews,
        corrections,
        currency,
        exchangeRateCdf,
      }),
    [corrections, currency, exchangeRateCdf, filteredTable, reviews],
  );

  useEffect(() => {
    if (!enabled) return;
    if (onRetainedTotalChange) {
      onRetainedTotalChange(retained > 0 ? retained : null);
    }
  }, [enabled, retained, onRetainedTotalChange]);

  useEffect(() => {
    if (!enabled) return;
    if (onReviewsValidationChange) {
      onReviewsValidationChange(validation ?? null);
    }
  }, [enabled, validation, onReviewsValidationChange]);

  if (!enabled) return null;

  const hasAnomalies = combinedTable.some(
    (row) => (row.type_obs ?? "ok") !== "ok" && !isSynthesisRow(row),
  );
  const anomalyCount = combinedTable.filter(
    (row) => (row.type_obs ?? "ok") !== "ok" && !isSynthesisRow(row),
  ).length;
  const pendingCount = validation?.pending ?? 0;

  const openRevised = () => void openAiReport(invoiceId, "revised");
  const openComplete = () => void openAiReport(invoiceId, "complete");

  const openCorrections = () => setShowCorrections(true);
  const openRefraction = () => {
    setRefractionError(null);
    openInvoiceRefractionSheet(invoiceId).catch((err: unknown) =>
      setRefractionError(err instanceof Error ? err.message : "Fiche de réfraction indisponible."),
    );
  };

  /** Ré-analyse complète : efface le travail enregistré → confirmation forte si validations présentes. */
  const confirmAndStartAnalysis = () => {
    const work = context?.saved_work;
    const saved = (work?.reviews ?? 0) + (work?.corrections ?? 0);
    if (saved === 0) {
      void startAnalysis();
      return;
    }
    const typed = window.prompt(
      `ATTENTION : la ré-analyse complète relit le PDF et EFFACE ${work?.reviews ?? 0} validation(s) ` +
        `et ${work?.corrections ?? 0} correction(s) déjà enregistrée(s). Le traitement repartira de zéro.\n\n` +
        "Pour recalculer les anomalies sans rien perdre, utilisez plutôt « Ré-analyser » dans la fenêtre de correction.\n\n" +
        "Tapez REANALYSER pour confirmer :",
    );
    if (typed?.trim().toUpperCase() === "REANALYSER") {
      void startAnalysis(true);
    }
  };

  return (
    <section className="theme-card overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-3 border-b border-border px-5 py-4 text-left"
      >
        <div className="flex items-center gap-2">
          <Brain size={18} className="text-primary" />
          <h2 className="text-base font-semibold text-foreground">Analyse IA</h2>
          {hasAnalysis && analysis?.score != null && (
            <span
              className={cn(
                "rounded-md px-2 py-0.5 text-[11px] font-semibold",
                analysis.score >= 80
                  ? "bg-green-100 text-green-800"
                  : analysis.score >= 60
                    ? "bg-amber-100 text-amber-800"
                    : "bg-red-100 text-red-800",
              )}
            >
              {analysis.score}% conforme
            </span>
          )}
        </div>
        {expanded ? <ChevronUp size={18} className="text-muted" /> : <ChevronDown size={18} className="text-muted" />}
      </button>

      {expanded && (
        <div className="space-y-4 p-5">
          {loading && !context && (
            <div className="flex items-center gap-2 text-sm text-muted">
              <Loader2 size={16} className="animate-spin" />
              Chargement de l&apos;analyse…
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}

          {rateBlocked && context?.rate_blocks_ai?.reason && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {context.rate_blocks_ai.reason}
            </div>
          )}

          {canDecide && context?.can_analyze && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={confirmAndStartAnalysis}
                disabled={analyzing || Boolean(rateBlocked)}
                className="inline-flex items-center gap-2 rounded-lg bg-foreground px-4 py-2 text-sm font-medium text-background hover:opacity-90 disabled:opacity-50"
              >
                {analyzing ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                {hasAnalysis ? "Ré-analyser avec l'IA" : "Analyser avec l'IA"}
              </button>
              {analyzing && (
                <p className="self-center text-xs text-muted">Analyse en arrière-plan… (plusieurs minutes possibles)</p>
              )}
            </div>
          )}

          {analysis?.extractor_fallback && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Extraction dégradée (OCR) — vérifiez la qualité du PDF.
            </div>
          )}

          {analysis?.summary && (
            <pre className="whitespace-pre-wrap rounded-lg bg-card-muted/50 p-3 text-xs leading-relaxed text-foreground">
              {analysis.summary}
            </pre>
          )}

          {hasAnalysis && analysis?.analyzed_at && (
            <p className="text-xs text-muted">
              Dernière analyse par {analysis.analyzed_by || "auditeur"} le{" "}
              {new Date(analysis.analyzed_at).toLocaleString("fr-FR")}
            </p>
          )}

          {combinedTable.length > 0 && (
            <>
              {canDecide && (
                <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-amber-950">
                        Actions auditeur — correction &amp; validation des actes IA
                      </p>
                      <p className="text-xs text-amber-900">
                        {anomalyCount} anomalie(s) détectée(s).
                        {pendingCount > 0 && (
                          <>
                            {" "}
                            <strong>{pendingCount}</strong> ligne(s) encore en attente de validation avant
                            approbation.
                          </>
                        )}
                      </p>
                      <div className="mt-2 grid gap-1 text-xs text-amber-950 sm:grid-cols-3">
                        <p>
                          Total facturé :{" "}
                          <strong>{formatMoney(moneySummary.billedTotal, currency)}</strong>
                        </p>
                        <p>
                          Montant retenu :{" "}
                          <strong className="text-green-800">
                            {formatMoney(moneySummary.retainedTotal, currency)}
                          </strong>
                        </p>
                        <p>
                          Montant réfracté :{" "}
                          <strong className="text-red-700">
                            {formatMoney(moneySummary.refractedTotal, currency)}
                          </strong>
                        </p>
                      </div>
                      {moneySummary.linesTotal > 0 &&
                        Math.abs(moneySummary.linesTotal - moneySummary.billedTotal) > 0.5 && (
                          <p className="text-[11px] text-amber-800">
                            Somme des actes (hors synthèse) :{" "}
                            {formatMoney(moneySummary.linesTotal, currency)}
                          </p>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={openCorrections}
                        className="inline-flex items-center gap-2 rounded-lg border-2 border-primary bg-white px-4 py-2.5 text-sm font-semibold text-primary shadow-sm hover:bg-primary/5"
                      >
                        <Pencil size={16} />
                        Corriger / valider les actes
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 border-t border-amber-200/80 pt-3">
                    {hasAnalysis && (
                      <button
                        type="button"
                        onClick={openRevised}
                        className="rounded-lg border border-border bg-white px-3 py-1.5 text-xs font-medium text-foreground hover:bg-card-muted"
                      >
                        Facture révisée
                      </button>
                    )}
                    {hasAnalysis && (
                      <button
                        type="button"
                        onClick={openComplete}
                        className="rounded-lg border border-border bg-white px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-card-muted"
                      >
                        Facture complète
                      </button>
                    )}
                    {hasAnalysis && canDecide && (
                      <button
                        type="button"
                        onClick={openRefraction}
                        disabled={pendingCount > 0}
                        title={
                          pendingCount > 0
                            ? "Disponible quand toutes les lignes sont validées"
                            : "Fiche de réfraction (impression / PDF / Excel depuis la fenêtre de correction)"
                        }
                        className="rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Fiche de réfraction
                      </button>
                    )}
                  </div>
                  {refractionError && <p className="mt-2 text-xs text-red-700">{refractionError}</p>}
                </div>
              )}

              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="mr-1 text-[11px] font-semibold uppercase tracking-wide text-muted">
                    Filtrer par observation
                  </span>
                  {(
                    [
                      { key: "all", label: "Tous" },
                      { key: "anomalies", label: "Anomalies" },
                      { key: "sans_proforma", label: "Sans proforma" },
                      { key: "surcharge", label: "Surcharge" },
                      { key: "sous_facturation", label: "Sous-fact." },
                      { key: "non_tarifé", label: "Non tarifé" },
                      { key: "ok", label: "OK" },
                    ] as const
                  ).map((f) => {
                    const count = obsFilterCounts[f.key] ?? 0;
                    if (f.key !== "all" && count === 0) return null;
                    return (
                      <button
                        key={f.key}
                        type="button"
                        onClick={() => setObsFilter(f.key)}
                        className={cn(
                          "rounded-full border px-2.5 py-1 text-[11px] font-medium",
                          obsFilter === f.key
                            ? "border-primary bg-primary/10 text-primary ring-1 ring-primary/30"
                            : "border-border text-muted-foreground hover:bg-card-muted",
                        )}
                      >
                        {f.label} ({count})
                      </button>
                    );
                  })}
                </div>

                <div className="overflow-x-auto rounded-xl border border-border">
                  <table className="w-full min-w-[960px] text-sm">
                    <thead>
                      <tr className="border-b border-border bg-card-muted/60 text-[11px] uppercase tracking-wide text-muted">
                        <th className="px-3 py-2 text-left">Bénéficiaire</th>
                        <th className="px-3 py-2 text-left">Date</th>
                        <th className="px-3 py-2 text-left">Acte</th>
                        <th className="px-3 py-2 text-center">Qté</th>
                        <th className="px-3 py-2 text-right">Facturé</th>
                        <th className="px-3 py-2 text-right">Grille</th>
                        <th className="px-3 py-2 text-right">Validé</th>
                        <th className="px-3 py-2 text-left">Statut</th>
                        <th className="px-3 py-2 text-left">Obs.</th>
                        {canDecide && hasAnomalies && (
                          <th className="px-3 py-2 text-center">Action</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTable.length === 0 && (
                        <tr>
                          <td
                            colSpan={canDecide && hasAnomalies ? 10 : 9}
                            className="px-3 py-6 text-center text-sm text-muted"
                          >
                            Aucune ligne pour ce filtre.
                          </td>
                        </tr>
                      )}
                      {filteredTable.map((row: InvoiceAiAnalysisRow, idx) => {
                        const qty = row.qty ?? 1;
                        const amounts = analysisRowAmounts(row, currency, exchangeRateCdf);
                        const isAnomaly = (row.type_obs ?? "ok") !== "ok";
                        const actIndex = row.act_orig_index ?? idx;
                        const review = reviews[actIndex];
                        const corr = corrections[actIndex];
                        const reviewStatus = review?.review_status ?? "pending";
                        let validatedLabel = "—";
                        let validatedClass = "text-muted";
                        if (reviewStatus === "approved") {
                          const v =
                            review?.montant_retenu != null && !Number.isNaN(review.montant_retenu)
                              ? Number(review.montant_retenu)
                              : amounts.total;
                          validatedLabel = formatMoney(v, currency);
                          validatedClass = "font-semibold text-green-700";
                        } else if (reviewStatus === "rejected") {
                          validatedLabel = formatMoney(0, currency);
                          validatedClass = "text-red-600";
                        } else if (corr?.corrected_price != null) {
                          const q = Math.max(1, corr.corrected_qty ?? qty);
                          validatedLabel = formatMoney(
                            Math.round(Number(corr.corrected_price) * q * 100) / 100,
                            currency,
                          );
                          validatedClass = "text-amber-700";
                        }
                        const statusLabel =
                          reviewStatus === "approved"
                            ? "Approuvé"
                            : reviewStatus === "rejected"
                              ? "Rejeté"
                              : "En attente";
                        return (
                          <tr
                            key={`${actIndex}-${idx}`}
                            className={cn(
                              "border-b border-border last:border-0",
                              isAnomaly && "bg-amber-50/40",
                            )}
                          >
                            <td className="px-3 py-2 text-foreground">{row.patient ?? "—"}</td>
                            <td className="px-3 py-2 text-foreground">
                              {row.date ? new Date(row.date).toLocaleDateString("fr-FR") : "—"}
                            </td>
                            <td className="px-3 py-2 text-foreground">
                              {row.act_name ?? "—"}
                              {row.is_manual && (
                                <span className="ml-1 rounded bg-sky-100 px-1 text-[9px] font-semibold uppercase not-italic text-sky-800">
                                  manuel
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {corr?.corrected_qty != null ? corr.corrected_qty : qty}
                            </td>
                            <td className="px-3 py-2 text-right">{formatMoney(amounts.total, currency)}</td>
                            <td className="px-3 py-2 text-right">
                              {amounts.grille > 0
                                ? formatMoney(amounts.grille * qty, currency)
                                : "—"}
                            </td>
                            <td className={cn("px-3 py-2 text-right", validatedClass)}>
                              {validatedLabel}
                              {reviewStatus === "pending" && corr?.corrected_price != null && (
                                <span className="mt-0.5 block text-[9px] font-normal not-italic text-amber-700">
                                  proposé
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <span
                                className={cn(
                                  "inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold",
                                  reviewStatus === "approved"
                                    ? "bg-green-100 text-green-800"
                                    : reviewStatus === "rejected"
                                      ? "bg-red-100 text-red-700"
                                      : "bg-amber-100 text-amber-800",
                                )}
                              >
                                {statusLabel}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              <span
                                className={cn(
                                  "inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase",
                                  obsClass(row.type_obs),
                                )}
                              >
                                {obsLabel(row.type_obs)}
                              </span>
                            </td>
                            {canDecide && hasAnomalies && (
                              <td className="px-3 py-2 text-center">
                                {isAnomaly ? (
                                  <button
                                    type="button"
                                    onClick={openCorrections}
                                    className="inline-flex items-center gap-1 rounded-md border border-primary/40 bg-white px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/5"
                                    title={`Corriger l'acte #${actIndex + 1}`}
                                  >
                                    <Pencil size={12} />
                                    Modifier
                                  </button>
                                ) : (
                                  <span className="text-[11px] text-muted">—</span>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                    {filteredTable.length > 0 && (
                      <tfoot>
                        <tr className="border-t-2 border-foreground/20 bg-card-muted/80 text-sm font-semibold text-foreground">
                          <td className="px-3 py-2.5" colSpan={3}>
                            Synthèse
                          </td>
                          <td className="px-3 py-2.5 text-center">{columnTotals.qty}</td>
                          <td className="px-3 py-2.5 text-right">
                            {formatMoney(columnTotals.billed, currency)}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            {columnTotals.grille > 0
                              ? formatMoney(columnTotals.grille, currency)
                              : "—"}
                          </td>
                          <td className="px-3 py-2.5 text-right text-green-800">
                            {formatMoney(columnTotals.validated, currency)}
                          </td>
                          <td className="px-3 py-2.5 text-muted">—</td>
                          <td className="px-3 py-2.5">
                            <span className="inline-flex rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-700">
                              Total
                            </span>
                          </td>
                          {canDecide && hasAnomalies && (
                            <td className="px-3 py-2.5 text-center text-muted">—</td>
                          )}
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
                <p className="text-[11px] text-muted">
                  {filteredTable.length} ligne(s) affichée(s)
                  {obsFilter !== "all" ? ` · filtre « ${obsFilter} »` : ""} · Colonne{" "}
                  <strong>Validé</strong> = montant retenu après validation auditeur (ou proposé après
                  correction manuelle).
                </p>
              </div>

              {canDecide && pendingCount > 0 && (
                <p className="text-xs text-amber-800">
                  {pendingCount} ligne(s) en attente — ouvrez <strong>Corriger / valider les actes</strong> pour
                  valider avant approbation.
                </p>
              )}
            </>
          )}

          {(analysis?.anomalies?.length ?? 0) > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-red-700">
                Anomalies ({analysis?.anomalies?.length})
              </h3>
              {analysis?.anomalies?.map((ano, i) => (
                <div key={i} className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-800">
                  <strong>{ano.act}</strong> — {ano.message}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <InvoiceAiCorrectionDialog
        open={showCorrections}
        invoiceId={invoiceId}
        hospitalId={hospitalId}
        table={combinedTable}
        corrections={corrections}
        reviews={reviews}
        currency={currency}
        exchangeRateCdf={exchangeRateCdf}
        withReviews={canDecide}
        saving={saving}
        reanalyzing={reanalyzing}
        dirty={dirty}
        saveState={saveState}
        saveError={error}
        billedTotal={context?.billed_total}
        onClose={() => {
          setShowCorrections(false);
          setRetainedPreview(null);
        }}
        onChange={updateCorrection}
        onReviewChange={updateReview}
        onSaveAll={saveAll}
        onSaveAndReanalyze={saveAndReanalyze}
        onOpenRefraction={() => openInvoiceRefractionSheet(invoiceId)}
        onDownloadRefraction={() => downloadInvoiceRefractionXlsx(invoiceId)}
        onAddManualRow={addManualRow}
        onRemoveManualRow={removeManualRow}
        onRetainedPreview={setRetainedPreview}
      />
    </section>
  );
}
